export const PANEL_ACTIVITY_DEFAULT_TIMEZONE = 'Europe/Madrid';
export const PANEL_ACTIVITY_DEFAULT_LIMIT = 10;
export const PANEL_ACTIVITY_LOG_PAGE_LIMIT = 30;

export type PanelActivityPeriod = 'today' | 'week' | 'month' | 'year';
export type PanelActivityDomain = 'requests' | 'budgets';
export type PanelActivityLogDomain = 'requests' | 'budgets';
export type PanelActivityLogActorType = 'admin' | 'client' | 'unknown';
export type PanelActivityLogActorFilter = 'admin' | 'client';
export type PanelActivityChartType = 'pie' | 'vertical_bar_stacked' | 'summary';

export type PanelRequestSegmentCode =
  | 'accepted'
  | 'completed'
  | 'cancelled'
  | 'pending'
  | 'incident_pending'
  | 'incident_resolved';

export type PanelBudgetSegmentCode =
  | 'sent'
  | 'pending'
  | 'incident_pending'
  | 'incident_resolved'
  | 'not_converted'
  | 'converted';

export type PanelActivitySegmentCode = PanelRequestSegmentCode | PanelBudgetSegmentCode;

export type PanelRequiredActionType =
  | 'pending_request_overdue'
  | 'incident_unresolved'
  | 'budget_not_converted'
  | 'budget_pending_overdue';

export type PanelRequiredActionSeverity = 'info' | 'warning' | 'high' | 'critical';
export type PanelRequiredActionStatus = 'open' | 'ignored' | 'manually_resolved' | 'auto_resolved';
export type PanelRequiredActionCommand = 'contact_client' | 'view_details';
export type PanelRequiredActionEntityType = 'request' | 'budget' | 'client';
export type PanelActivityLogEntityType = 'request' | 'budget';

export type PanelActivityLogActionCode =
  | 'request_created'
  | 'request_created_from_budget'
  | 'request_edited'
  | 'request_cancelled'
  | 'request_accepted'
  | 'request_completed'
  | 'request_incident_opened'
  | 'request_incident_resolved'
  | 'request_status_changed'
  | 'request_deleted'
  | 'budget_created'
  | 'budget_edited'
  | 'budget_sent'
  | 'budget_incident_opened'
  | 'budget_incident_resolved'
  | 'budget_status_changed'
  | 'budget_deleted';

export interface PanelActivityTimeWindow {
  start_at: string;
  end_at: string;
  label: string;
}

export interface PanelActivityThresholds {
  budget_not_converted_hours: number;
}

export interface PanelActivityStatusResponse {
  generated_at: string;
  timezone: string;
  pending_requests: number;
  request_incidents: number;
  budget_incidents: number;
  active_clients_today: number;
  pending_budgets: number;
  thresholds: PanelActivityThresholds;
}

export interface PanelActivityRatio {
  code: string;
  label: string;
  numerator: number;
  denominator: number;
  percentage: number;
}

export interface PanelActivityVolumeSegment {
  code: PanelActivitySegmentCode;
  label: string;
  count: number;
  percentage: number;
}

export interface PanelActivityVolumePoint {
  period_start: string;
  period_end: string;
  label: string;
  total: number;
  segments: PanelActivityVolumeSegment[];
}

export interface PanelActivityVolumeResponse {
  generated_at: string;
  timezone: string;
  period: PanelActivityPeriod;
  domain: PanelActivityDomain;
  chart_type: PanelActivityChartType;
  window: PanelActivityTimeWindow;
  total: number;
  segments: PanelActivityVolumeSegment[];
  points: PanelActivityVolumePoint[];
  ratios: PanelActivityRatio[];
}

export interface PanelActivityRequestVolumeResponse extends PanelActivityVolumeResponse {
  domain: 'requests';
}

export interface PanelActivityBudgetVolumeResponse extends PanelActivityVolumeResponse {
  domain: 'budgets';
}

export interface PanelActivityTopClientBreakdownItem {
  code: PanelActivitySegmentCode | string;
  label: string;
  count: number;
}

export interface PanelActivityTopClientItem {
  position: number;
  client_id: string;
  client_name: string;
  total: number;
  percentage: number;
  breakdown: PanelActivityTopClientBreakdownItem[];
}

export interface PanelActivityTopClientsResponse {
  generated_at: string;
  timezone: string;
  period: PanelActivityPeriod;
  domain: PanelActivityDomain;
  window: PanelActivityTimeWindow;
  limit: number;
  items: PanelActivityTopClientItem[];
}

export interface PanelActivityLogActor {
  id: string;
  name: string;
  type: PanelActivityLogActorType;
}

export interface PanelActivityLogItem {
  id: string;
  occurred_at: string;
  domain: PanelActivityLogDomain;
  entity_type: PanelActivityLogEntityType;
  entity_id: string;
  action_code: PanelActivityLogActionCode;
  action_label: string;
  actor: PanelActivityLogActor;
  previous_state: string | null;
  new_state: string | null;
  comment: string | null;
  metadata: Record<string, unknown>;
}

export interface PanelActivityLogResponse {
  generated_at: string;
  timezone: string;
  domain: PanelActivityLogDomain;
  limit: typeof PANEL_ACTIVITY_LOG_PAGE_LIMIT;
  next_cursor: string | null;
  items: PanelActivityLogItem[];
}

export interface PanelActivityRequestsLogResponse extends PanelActivityLogResponse {
  domain: 'requests';
}

export interface PanelActivityBudgetsLogResponse extends PanelActivityLogResponse {
  domain: 'budgets';
}

export interface PanelRequiredActionItem {
  id: string;
  type: PanelRequiredActionType;
  severity: PanelRequiredActionSeverity;
  priority_score: number;
  client_id: string;
  client_name: string;
  entity_type: PanelRequiredActionEntityType;
  entity_id: string;
  title: string;
  description: string;
  age_hours: number;
  created_at: string;
  last_update_at: string;
  badges: string[];
  actions: PanelRequiredActionCommand[];
  state: PanelRequiredActionStatus | null;
}

export interface PanelRequiredActionsResponse {
  generated_at: string;
  timezone: string;
  limit: number;
  include_ignored: boolean;
  items: PanelRequiredActionItem[];
}

export interface PanelRequiredActionStateUpdateResponse {
  action_key: string;
  status: PanelRequiredActionStatus;
  ignored_until: string | null;
  resolved_at: string | null;
  updated_by: string;
  updated_at: string;
}

export type PanelActivityDateValue = string | Date;

export interface PanelActivityPeriodParams {
  period?: PanelActivityPeriod;
  date?: PanelActivityDateValue | null;
  timezone?: string | null;
}

export interface PanelActivityTopClientsParams extends PanelActivityPeriodParams {
  limit?: number | null;
}

export interface PanelActivityLogParams {
  cursor?: string | null;
  actorType?: PanelActivityLogActorFilter | null;
  timezone?: string | null;
}

export interface PanelRequiredActionsParams {
  limit?: number | null;
  includeIgnored?: boolean | null;
  timezone?: string | null;
}
